import * as React from "react";
import { Button, Gapped, Link, Modal } from "react-ui";

import {
    ACCEPT_LOAD_TEMPLATE_CLICK,
    ACCEPT_RENAME_TEMPLATE_CLICK,
    CANCEL_LOAD_TEMPLATE_CLICK,
    CANCEL_RENAME_TEMPLATE_CLICK,
    DELETE_TEMPLATE_CLICK,
    LOAD_TEMPLATE_CLICK,
    RENAME_TEMPLATE_CLICK,
    RESTORE_TEMPLATE_CLICK,
} from "../../../metricsAction";
import { useTemplatesApi } from "../../../Internals/Contexts/TemplatesApiContext";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { Icon } from "../../../Icons/Icon";

import { restoreTemplate, validationNameOnSave } from "./TemplatesHelpers";
import { TemplateEditableItem } from "./TemplateEditableItem";
import {
    Caption,
    ContainerText,
    DeleteLink,
    LoadedText,
    RenameLink,
    TemplateDelete,
    TemplateDeleteText,
} from "./TemplateItem.styles";

interface TemplateItemProps {
    guid: string;
    name: string;
    highlight?: boolean;
    onMouseEnter?: () => void;
    onSaveForm: () => Promise<void>;
    gfv: string;
    draftId: string;
}

export function TemplateItem(props: TemplateItemProps): JSX.Element {
    const templatesApi = useTemplatesApi();
    const track = useMetrics();

    const [name, setName] = React.useState(props.name);
    const [showLoadTemplateModal, setShowLoadTemplateModal] = React.useState(false);
    const [view, setView] = React.useState<"text" | "rename" | "delete">("text");
    const [errorMessage, setErrorMessage] = React.useState<undefined | string>(undefined);

    const handleOnClickTemplateName = () => {
        track(LOAD_TEMPLATE_CLICK);
        setShowLoadTemplateModal(true);
    };

    const handleOnClose = () => {
        track(CANCEL_LOAD_TEMPLATE_CLICK);
        setShowLoadTemplateModal(false);
    };

    const handleOnClickLoad = async () => {
        track(ACCEPT_LOAD_TEMPLATE_CLICK);
        try {
            await props.onSaveForm();
            await templatesApi.loadTemplate({
                guid: props.guid,
                draftId: props.draftId,
                gfv: props.gfv,
            });
            window.location.reload();
        } catch {
            // eslint-disable-next-line no-alert
            alert("Не удалось загрузить шаблон. Попробуйте позже.");
        }
    };

    const handleRename = (event?: React.MouseEvent) => {
        track(RENAME_TEMPLATE_CLICK);
        if (event != undefined) {
            event.stopPropagation();
        }
        setView("rename");
    };

    const handleCancelRename = () => {
        track(CANCEL_RENAME_TEMPLATE_CLICK);
        setView("text");
        setErrorMessage(undefined);
    };

    const handleApplyRename = async (value: string) => {
        track(ACCEPT_RENAME_TEMPLATE_CLICK, { newName: value, oldName: name });
        const templateName = name;
        const newName = value.trim();

        if (newName === templateName) {
            setView("text");
            setErrorMessage(undefined);
        } else {
            const error = await validationNameOnSave(templatesApi, newName, props.gfv);
            if (error) {
                setErrorMessage(error);
            } else {
                try {
                    await templatesApi.renameTemplate({
                        templateName: newName,
                        guid: props.guid,
                        gfv: props.gfv,
                    });
                    setName(newName);
                    setErrorMessage(undefined);
                    setView("text");
                } catch {
                    // eslint-disable-next-line no-alert
                    alert("Не удалось переименовать шаблон. Попробуйте позже.");
                }
            }
        }
    };

    const handleDelete = async (event?: React.MouseEvent) => {
        track(DELETE_TEMPLATE_CLICK);
        if (event != undefined) {
            event.stopPropagation();
        }

        try {
            await templatesApi.deleteTemplate({ guid: props.guid, gfv: props.gfv });
            setView("delete");
        } catch {
            // eslint-disable-next-line no-alert
            alert("Не удалось удалить шаблон.");
        }
    };

    const handleCancelDelete = async (event?: React.MouseEvent) => {
        track(RESTORE_TEMPLATE_CLICK);
        if (event != undefined) {
            event.stopPropagation();
        }
        await restoreTemplate(templatesApi, name, props.guid, props.gfv);
        setView("text");
    };

    const handleOnMouseEnter = () => {
        if (props.highlight) {
            props.onMouseEnter?.();
        }
    };

    const renderLoadTemplateModal = (): JSX.Element => (
        <Modal ignoreBackgroundClick width={440} onClose={handleOnClose}>
            <Modal.Header>
                <LoadedText>Загрузить данные из шаблона «{name}»?</LoadedText>
            </Modal.Header>
            <Modal.Body>Все текущие данные будут заменены данными из шаблона</Modal.Body>
            <Modal.Footer panel>
                <Gapped gap={16}>
                    <Button size="medium" use="primary" onClick={handleOnClickLoad}>
                        Да, загрузить
                    </Button>
                    <Button size="medium" onClick={handleOnClose}>
                        Отменить
                    </Button>
                </Gapped>
            </Modal.Footer>
        </Modal>
    );

    const renderTextView = (): JSX.Element => (
        <ContainerText highlighted={props.highlight} onClick={handleOnClickTemplateName}>
            <Caption>{name}</Caption>
            <RenameLink>
                <Link
                    onClick={handleRename}
                    icon={<Icon name="ToolPencilLineIcon" />}
                    data-tid={`RenameTemplate-${name}`}>
                    Переименовать
                </Link>
            </RenameLink>
            <DeleteLink>
                <Link onClick={handleDelete} icon={<Icon name="XIcon" />}>
                    {/* баг с react-ui при стандартном margin 4 в link сочетание с крестиком (иконка удалить) выглядит огромным пробелом */}
                    <span style={{ marginLeft: -3 }} data-tid={`DeleteTemplate-${name}`}>
                        Удалить
                    </span>
                </Link>
            </DeleteLink>
        </ContainerText>
    );

    const renderRenameView = (): JSX.Element => (
        <TemplateEditableItem
            errorMessage={errorMessage}
            value={name}
            onClickCancel={handleCancelRename}
            onClickSave={handleApplyRename}
        />
    );

    const renderDeleteView = (): JSX.Element => (
        <TemplateDelete>
            <TemplateDeleteText>Шаблон «{adjustNameLength(name, 35)}» удален</TemplateDeleteText>
            <Link onClick={handleCancelDelete}>Отменить</Link>
        </TemplateDelete>
    );

    const renderItem = (): JSX.Element => {
        switch (view) {
            case "text":
                return renderTextView();
            case "rename":
                return renderRenameView();
            case "delete":
                return renderDeleteView();
            default:
                return renderTextView();
        }
    };

    return (
        <div onMouseEnter={handleOnMouseEnter}>
            {showLoadTemplateModal && renderLoadTemplateModal()}
            {renderItem()}
        </div>
    );
}

function adjustNameLength(str: string, length: number): string {
    if (str.length > length) {
        return str.substring(0, length - 2) + "...";
    }
    return str;
}
